import {
  ceilToStartOfDay,
  endOfContainingDay,
  type DateRange,
} from "@repo/app-types/entities/dateRange";
import { DateTime } from "luxon";

/** Human-readable label for a {@link DateRange} value — either a preset like
 * "Last 30 days" or a specific start/end date range. */
export function formatDateRange(value: DateRange): string {
  if (value.end !== undefined) {
    const [startDate, endDate] = computeDayBoundaries(value.end, value);
    return `${startDate.toLocaleString(DateTime.DATE_SHORT)} – ${endDate.toLocaleString(DateTime.DATE_SHORT)}`;
  }

  const days = value.length.as("days");
  if (days > 1.5) {
    return `Last ${Math.round(days)} days`;
  }

  const hours = value.length.as("hours");
  return `Last ${Math.round(hours)} hours`;
}

/**
 * Like {@link formatDateRange}, but anchored ranges render verbose endpoints
 * with weekday names ("Mon Jun 22 – Mon Jun 29"), eliding the year when both
 * endpoints share it. For surfaces where the range is the focal label (e.g.
 * the weekly coaching goal header) rather than a compact filter value.
 */
export function formatDateRangeWithWeekdays(value: DateRange): string {
  if (value.end === undefined) {
    return formatDateRange(value);
  }

  const [startDate, endDate] = computeDayBoundaries(value.end, value);
  return `${formatWeekdayEndpoint(startDate, endDate)} – ${formatWeekdayEndpoint(endDate, startDate)}`;
}

// Mirrors the query-side snapping (both bounds ceil to day boundaries) so
// the label always names the same days the range actually covers.
function computeDayBoundaries(
  end: DateTime<true>,
  value: DateRange,
): [DateTime, DateTime] {
  return [ceilToStartOfDay(end.minus(value.length)), endOfContainingDay(end)];
}

function formatWeekdayEndpoint(date: DateTime, otherDate: DateTime): string {
  return date.toFormat(
    date.year === otherDate.year ? "EEE MMM d" : "EEE MMM d, yyyy",
  );
}
